/**
 * POS-SUPPLIERS-CXP-DASHBOARD-001C — Vista consolidada Cuentas por Pagar (CxP)
 * Reutiliza motor/API existentes. No es ledger ni CxC.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaExclamationTriangle,
  FaClock,
  FaTruck,
  FaBoxOpen,
  FaHandHoldingUsd,
  FaFileInvoiceDollar,
  FaSync,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { matrixAllows } from '../../utils/matrixAllows';
import proveedoresService from '../../services/proveedores.service';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Table from '../../components/common/Table';

const FILTERS = [
  { id: 'todos', label: 'Todos' },
  { id: 'con_deuda', label: 'Con deuda' },
  { id: 'vencidos', label: 'Vencidos' },
  { id: 'proximos', label: 'Próximos' },
  { id: 'anticipos', label: 'Con anticipos' },
];

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return v.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function estadoLabel(estado) {
  if (estado === 'vencido') return 'Vencido';
  if (estado === 'por_vencer') return 'Por vencer';
  return 'Al día';
}

function estadoClass(estado) {
  if (estado === 'vencido') return 'text-e0e3-danger border-e0e3-danger/30 bg-e0e3-danger/10';
  if (estado === 'por_vencer') return 'text-e0e3-warn border-e0e3-warn/30 bg-e0e3-warn/10';
  return 'text-e0e3-ok border-e0e3-ok/30 bg-e0e3-ok/10';
}

const CuentasPorPagar = () => {
  const { hasPermission, permisosEfectivos } = useAuth();
  const navigate = useNavigate();
  const canView =
    matrixAllows(permisosEfectivos, 'proveedores', 'cuenta_corriente.ver') === true ||
    hasPermission('proveedores', 'cuenta_corriente.ver') === true;
  const canPay = matrixAllows(permisosEfectivos, 'compras', 'pagar') === true;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [nombres, setNombres] = useState({});
  const [filter, setFilter] = useState('todos');
  const [kpiErrors, setKpiErrors] = useState({});
  const tableRef = useRef(null);
  const unbilledRef = useRef(null);
  const inflight = useRef(false);

  const cargar = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      setSummary(null);
      return;
    }
    if (inflight.current) return;
    inflight.current = true;
    try {
      setError(null);
      setKpiErrors({});
      const data = await proveedoresService.obtenerResumenCuentasPorPagar();
      setSummary(data || {});
      try {
        const lista = await proveedoresService.obtenerTodos();
        const map = {};
        (lista || []).forEach((p) => {
          if (p?.id) map[p.id] = p.nombre || p.razon_social || p.id;
        });
        setNombres(map);
      } catch {
        /* nombres opcionales */
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setError('forbidden');
      } else {
        setError(e?.response?.data?.message || e.message || 'No pudimos cargar las cuentas por pagar.');
      }
      setSummary(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
      inflight.current = false;
    }
  }, [canView]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  const onRefresh = async () => {
    if (inflight.current) return;
    setRefreshing(true);
    await cargar();
  };

  const proveedores = summary?.proveedores || [];
  const compromisos = summary?.compromisos || [];

  const filtered = useMemo(() => {
    return proveedores.filter((p) => {
      const saldo = Number(p.saldoPendiente || 0);
      const vencido = Number(p.saldoVencido || 0);
      const ant = Number(p.anticiposDisponibles || 0);
      const dueSoon = Number(p.deudaProximoVencer || 0);
      if (filter === 'con_deuda') return saldo > 0;
      if (filter === 'vencidos') return vencido > 0;
      if (filter === 'proximos') return dueSoon > 0;
      if (filter === 'anticipos') return ant > 0;
      return true;
    });
  }, [proveedores, filter]);

  const hoyAlerts = useMemo(() => {
    if (!summary) return [];
    const alerts = [];
    const vencida = Number(summary.deudaVencida || 0);
    const prox = Number(summary.deudaProximoVencer || 0);
    const proxCount = Number(summary.facturasProximasCount || 0);
    const sinFac = Number(summary.recibidoSinFacturaCount ?? summary.compromisosSinFactura ?? 0);
    const ant = Number(summary.anticiposDisponibles || 0);

    if (vencida > 0) {
      alerts.push({
        id: 'vencidas',
        text: `Tenés facturas vencidas por ${money(vencida)}.`,
        cta: 'Revisar vencidas',
        onClick: () => setFilter('vencidos'),
      });
    }
    if (prox > 0 || proxCount > 0) {
      alerts.push({
        id: 'proximas',
        text:
          proxCount > 0
            ? `${proxCount} factura${proxCount === 1 ? '' : 's'} vencen durante los próximos 7 días${
                prox > 0 ? ` (${money(prox)})` : ''
              }.`
            : `Hay vencimientos en los próximos 7 días por ${money(prox)}.`,
        cta: 'Ver próximos',
        onClick: () => setFilter('proximos'),
      });
    }
    if (sinFac > 0) {
      alerts.push({
        id: 'sin_factura',
        text: `Hay ${sinFac} compra${sinFac === 1 ? '' : 's'} recibida${sinFac === 1 ? '' : 's'} pendiente${
          sinFac === 1 ? '' : 's'
        } de factura.`,
        cta: 'Ver sin factura',
        onClick: () => unbilledRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      });
    }
    if (alerts.length < 3 && ant > 0) {
      alerts.push({
        id: 'anticipos',
        text: `Tenés ${money(ant)} en anticipos disponibles.`,
        cta: 'Ver anticipos',
        onClick: () => setFilter('anticipos'),
      });
    }
    return alerts.slice(0, 3);
  }, [summary]);

  const renderKpiValue = (key, value, isMoney = true) => {
    if (loading) {
      return <span className="inline-block h-7 w-24 rounded bg-white/[0.06] animate-pulse" aria-hidden />;
    }
    if (kpiErrors[key]) {
      return <span className="text-base font-semibold text-e0e3-muted">No disponible</span>;
    }
    if (value == null || Number.isNaN(Number(value))) {
      return <span className="text-base font-semibold text-e0e3-muted">No disponible</span>;
    }
    if (isMoney) return <span className="text-xl font-bold text-e0e3-foreground">{money(value)}</span>;
    return <span className="text-xl font-bold text-e0e3-foreground">{Number(value)}</span>;
  };

  const kpis = [
    {
      id: 'deudaTotal',
      title: 'Deuda total',
      icon: <FaFileInvoiceDollar />,
      value: summary?.deudaTotal,
      money: true,
      onClick: () => setFilter('con_deuda'),
      tone: 'default',
    },
    {
      id: 'deudaVencida',
      title: 'Deuda vencida',
      icon: <FaExclamationTriangle />,
      value: summary?.deudaVencida,
      money: true,
      onClick: () => setFilter('vencidos'),
      tone: 'danger',
    },
    {
      id: 'deudaProximoVencer',
      title: 'Vence en 7 días',
      icon: <FaClock />,
      value: summary?.deudaProximoVencer,
      money: true,
      onClick: () => setFilter('proximos'),
      tone: 'warn',
    },
    {
      id: 'proveedoresConDeuda',
      title: 'Proveedores con deuda',
      icon: <FaTruck />,
      value: summary?.proveedoresConDeuda,
      money: false,
      onClick: () => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      tone: 'default',
    },
    {
      id: 'recibidoSinFacturaCount',
      title: 'Mercadería recibida sin factura',
      icon: <FaBoxOpen />,
      value: summary?.recibidoSinFacturaCount ?? summary?.compromisosSinFactura,
      money: false,
      hint: 'No forma parte de la deuda hasta registrar la factura.',
      onClick: () => unbilledRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      tone: 'default',
    },
    {
      id: 'anticiposDisponibles',
      title: 'Anticipos disponibles',
      icon: <FaHandHoldingUsd />,
      value: summary?.anticiposDisponibles,
      money: true,
      onClick: () => setFilter('anticipos'),
      tone: 'default',
    },
  ];

  const columns = [
    {
      header: 'Proveedor',
      accessor: 'supplierId',
      cell: (row) => (
        <span className="font-medium text-e0e3-foreground" title={nombres[row.supplierId] || row.supplierId}>
          {nombres[row.supplierId] || row.supplierId}
        </span>
      ),
    },
    {
      header: 'Estado',
      accessor: 'estado',
      cell: (row) => (
        <span className={`inline-flex px-2 py-0.5 rounded border text-xs font-medium ${estadoClass(row.estado)}`}>
          {estadoLabel(row.estado)}
        </span>
      ),
    },
    {
      header: 'Saldo',
      accessor: 'saldoPendiente',
      cell: (row) => money(row.saldoPendiente) || '—',
    },
    {
      header: 'Vencido',
      accessor: 'saldoVencido',
      cell: (row) => (
        <span className={Number(row.saldoVencido) > 0 ? 'text-e0e3-danger font-medium' : ''}>
          {money(row.saldoVencido) || '—'}
        </span>
      ),
    },
    {
      header: 'Próximo vencimiento',
      accessor: 'proximoVencimiento',
      cell: (row) => formatFecha(row.proximoVencimiento),
    },
    {
      header: 'Anticipos',
      accessor: 'anticiposDisponibles',
      cell: (row) => money(row.anticiposDisponibles) || '—',
    },
    {
      header: 'Acción',
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            color="secondary"
            onClick={() => navigate(`/proveedores/${row.supplierId}/cuenta-corriente`)}
          >
            Ver cuenta
          </Button>
          {canPay && (
            <Button
              size="sm"
              color="primary"
              onClick={() =>
                navigate(`/proveedores/${row.supplierId}/cuenta-corriente?tab=acciones&action=pago`)
              }
            >
              Agregar pago
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-e0e3-foreground">Cuentas por pagar</h1>
        <Card>
          <p className="text-e0e3-muted">
            No tenés permisos para ver información financiera de proveedores.
          </p>
          <p className="text-xs text-e0e3-muted mt-2">
            Se requiere <code>proveedores.cuenta_corriente.ver</code>.
          </p>
        </Card>
      </div>
    );
  }

  if (loading && !summary) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Spinner size="lg" />
        <p className="text-sm text-e0e3-muted">Cargando cuentas por pagar…</p>
      </div>
    );
  }

  if (error === 'forbidden') {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-e0e3-foreground">Cuentas por pagar</h1>
        <Card>
          <p className="text-e0e3-muted">
            No tenés permisos para ver información financiera de proveedores.
          </p>
        </Card>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-e0e3-foreground">Cuentas por pagar</h1>
        <Card>
          <p className="text-e0e3-muted mb-3">No pudimos cargar las cuentas por pagar.</p>
          <p className="text-xs text-e0e3-muted mb-4">{error}</p>
          <Button color="primary" onClick={onRefresh} loading={refreshing}>
            Reintentar
          </Button>
        </Card>
      </div>
    );
  }

  const emptyDebt =
    !loading &&
    Number(summary?.deudaTotal || 0) === 0 &&
    Number(summary?.recibidoSinFacturaCount ?? summary?.compromisosSinFactura ?? 0) === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">Cuentas por pagar</h1>
          <p className="text-sm text-e0e3-muted mt-1">
            Resumen financiero consolidado de proveedores
          </p>
        </div>
        <Button
          color="secondary"
          size="sm"
          icon={<FaSync />}
          onClick={onRefresh}
          loading={refreshing}
          disabled={refreshing}
        >
          Actualizar
        </Button>
      </div>

      {hoyAlerts.length > 0 && (
        <div className="rounded-2xl border border-e0e3-accent/30 bg-e0e3-accent/10 px-4 py-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-e0e3-accentSoft">Hoy</p>
          {hoyAlerts.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-e0e3-foreground">{a.text}</p>
              <Button size="sm" color="primary" onClick={a.onClick}>
                {a.cta}
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {kpis.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={k.onClick}
            className={`text-left nexo-surface p-4 transition hover:bg-white/[0.03] focus:outline-none focus:ring-2 focus:ring-e0e3-accent/40 ${
              k.tone === 'danger' ? 'border-e0e3-danger/20' : k.tone === 'warn' ? 'border-e0e3-warn/20' : ''
            }`}
          >
            <div className="flex items-center gap-2 text-e0e3-muted text-xs mb-2">
              <span className="text-e0e3-accent">{k.icon}</span>
              <span>{k.title}</span>
            </div>
            {renderKpiValue(k.id, k.value, k.money)}
            {k.hint && <p className="text-[11px] text-e0e3-muted mt-2 leading-snug">{k.hint}</p>}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtros cuentas por pagar">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded text-sm border transition ${
              filter === f.id
                ? 'border-e0e3-accent bg-e0e3-accent/10 text-e0e3-foreground'
                : 'border-white/10 text-e0e3-muted hover:bg-white/[0.04]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div ref={tableRef}>
        <Card title="Proveedores" icon={<FaTruck />}>
          {emptyDebt ? (
            <div className="text-center py-8 space-y-3">
              <p className="text-e0e3-muted">No existen deudas pendientes con proveedores.</p>
              <div className="flex justify-center gap-2 flex-wrap">
                <Link to="/proveedores">
                  <Button size="sm" color="secondary">
                    Proveedores
                  </Button>
                </Link>
                <Link to="/compras">
                  <Button size="sm" color="secondary">
                    Compras
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <Table columns={columns} data={filtered} pagination itemsPerPage={15} />
          )}
        </Card>
      </div>

      <div ref={unbilledRef}>
        <Card title="Mercadería recibida sin factura" icon={<FaBoxOpen />}>
          <p className="text-xs text-e0e3-muted mb-4">
            Estas compras todavía no forman parte de la deuda contable.
          </p>
          {!compromisos.length ? (
            <p className="text-sm text-e0e3-muted">No hay compras recibidas pendientes de factura.</p>
          ) : (
            <ul className="space-y-2">
              {compromisos.map((c) => {
                const provId = c.proveedor_id || c.proveedorId || c.supplierId;
                const total = c.total != null ? money(c.total) : null;
                return (
                  <li
                    key={c.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 px-3 py-2"
                  >
                    <div className="min-w-0 text-sm">
                      <p className="font-medium text-e0e3-foreground truncate">
                        Compra {c.numero || c.id}
                      </p>
                      <p className="text-e0e3-muted text-xs">
                        {nombres[provId] || provId || 'Proveedor'} · recepción{' '}
                        {formatFecha(c.fecha_recepcion || c.fechaRecepcion || c.updatedAt)}
                        {total ? ` · ref. ${total}` : ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      color="primary"
                      disabled={!provId}
                      onClick={() =>
                        navigate(`/proveedores/${provId}/cuenta-corriente?tab=acciones`, {
                          state: {
                            compra_id: c.id,
                            total_sugerido: c.total,
                            tab: 'acciones',
                          },
                        })
                      }
                    >
                      Registrar factura
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CuentasPorPagar;
